import { Call } from '@twilio/voice-sdk';
import { CronoCallEvent } from './cronoCallReducer';
import { useAuth } from 'context/auth';
import useGetTwilioToken from 'hooks/services/call/useGetTwilioToken';
import { useTwilioDevice } from './useTwilioDevice';
import { useEffect } from 'react';
import { bindCallEventListeners } from './bindCallEventListeners';
import { hasActiveAudioHelper, hasActiveCall } from './guards';
import { useSnackbar } from 'context/snackbar';
import { captureException } from 'utils/exceptionUtils';

const CRONO_VOIP_MICROPHONE_DEVICE_ID_KEY = 'crono_voip_microphone_device_id';
const CRONO_VOIP_SPEAKER_DEVICE_ID_KEY = 'crono_voip_speaker_device_id';

export const useTwilioCallSession = ({
  send,
  callRef,
  wasRecorded,
}: {
  send: React.Dispatch<CronoCallEvent>;
  callRef: React.MutableRefObject<Call | undefined>;
  wasRecorded: React.MutableRefObject<boolean>;
}) => {
  const { user } = useAuth();
  const { mutateAsync: getToken } = useGetTwilioToken();
  const { device, setupDevice, destroyDevice } = useTwilioDevice();
  const { pushSnackbar } = useSnackbar();

  useEffect(() => {
    return () => {
      destroyDevice();
    };
  }, []);

  const startCall = async ({
    fromPhoneNumber,
    callee,
    onDisconnect,
  }: {
    fromPhoneNumber: string;
    callee: {
      id: number;
      firstName?: string;
      lastName?: string;
      website?: string;
      phoneNumber: string;
    };
    onDisconnect?: (params: { call: Call; wasRecorded: boolean }) => void;
  }) => {
    try {
      if (!user) {
        throw new Error('User not found.');
      }
      if (callRef.current && callRef.current.status() !== 'closed') {
        throw new Error('A call is already in progress.');
      }

      const tokenResponse = await getToken({
        fromPhoneNumber,
        toPhoneNumber: callee.phoneNumber,
      });

      const { token, edge } = tokenResponse.data.data;

      const localDevice = setupDevice(token, edge);

      const savedMicrophoneId = localStorage.getItem(
        CRONO_VOIP_MICROPHONE_DEVICE_ID_KEY,
      );
      const savedSpeakerId = localStorage.getItem(
        CRONO_VOIP_SPEAKER_DEVICE_ID_KEY,
      );

      localDevice.on('deviceChange', () => {
        send({
          type: 'availableInputDevicesChanged',
          availableInputDevices: localDevice.audio?.availableInputDevices,
        });

        send({
          type: 'availableOutputDevicesChanged',
          availableOutputDevices: localDevice.audio?.availableOutputDevices,
        });
      });

      send({
        type: 'callConnecting',
        callee,
        availableInputDevices: localDevice.audio?.availableInputDevices,
        availableOutputDevices: localDevice.audio?.availableOutputDevices,
        selectedInputDevice: localDevice.audio?.inputDevice ?? undefined,
        activeSpeakerDevices: localDevice.audio?.speakerDevices,
        areSettingsOpen: false,
      });

      wasRecorded.current = false;

      callRef.current = await localDevice.connect({
        params: {
          // caller
          callerPhone: fromPhoneNumber,
          subscriptionId: user.subscriptionId.toString(),
          userId: user.id.toString(),
          // callee
          To: callee.phoneNumber,
        },
      });

      bindCallEventListeners({
        call: callRef.current,
        send,
        onDisconnect: (params) => {
          onDisconnect?.(params);
          if (!hasActiveAudioHelper(device.current?.audio)) return;
          device.current.audio.unsetInputDevice();
        },
        wasRecorded,
        onError: (error) => {
          pushSnackbar({
            severity: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Unknown error during call.',
          });
        },
      });

      if (savedMicrophoneId) {
        try {
          await changeMicrophone(savedMicrophoneId);
        } catch (error) {
          console.error(
            '[useCronoCall] Failed to set saved microphone:',
            error,
          );
          captureException(error, {
            level: 'error',
          });
          localStorage.removeItem(CRONO_VOIP_MICROPHONE_DEVICE_ID_KEY);
        }
      }

      if (savedSpeakerId) {
        try {
          await changeSpeaker(savedSpeakerId);
        } catch (error) {
          console.error('[useCronoCall] Failed to set saved speaker:', error);
          captureException(error, {
            level: 'error',
          });
          localStorage.removeItem(CRONO_VOIP_SPEAKER_DEVICE_ID_KEY);
        }
      }
    } catch (error) {
      console.error('[useCronoCall] Error starting call:', error);
      pushSnackbar({
        severity: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Unknown error starting call.',
      });
      send({ type: 'callErrored' });
      captureException(error, {
        level: 'error',
      });
    }
  };

  const changeMicrophone = async (deviceId: string) => {
    if (!hasActiveAudioHelper(device.current?.audio)) return;
    await device.current.audio.setInputDevice(deviceId);
    send({
      type: 'selectedInputDeviceChanged',
      selectedInputDevice: device.current.audio.inputDevice ?? undefined,
    });
    localStorage.setItem(CRONO_VOIP_MICROPHONE_DEVICE_ID_KEY, deviceId);
  };

  const changeSpeaker = async (deviceId: string) => {
    if (!hasActiveAudioHelper(device.current?.audio)) return;
    await device.current.audio.speakerDevices.set(deviceId);
    send({
      type: 'activeSpeakerDevicesChanged',
      activeSpeakerDevices: device.current.audio.speakerDevices,
    });
    localStorage.setItem(CRONO_VOIP_SPEAKER_DEVICE_ID_KEY, deviceId);
  };

  const hangup = () => {
    if (!hasActiveCall(callRef.current)) return;
    callRef.current.disconnect();
  };

  const mute = (shouldMute: boolean) => {
    if (!hasActiveCall(callRef.current)) return;
    callRef.current.mute(shouldMute);
  };

  const playDTMF = (digits: string) => {
    if (!hasActiveCall(callRef.current)) return;
    callRef.current.sendDigits(digits);
  };

  return {
    startCall,
    changeMicrophone,
    changeSpeaker,
    hangup,
    mute,
    playDTMF,
  };
};
